import React, { createContext, useContext, useReducer } from 'react';
import { User } from '../types';

interface UserState {
  user: User | null;
  isOnboarded: boolean;
  currentStep: number;
}

type UserAction =
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_STOCK_TYPES'; payload: string[] }
  | { type: 'SET_BIRTH_DATE'; payload: { year: number; month: number } }
  | { type: 'COMPLETE_ONBOARDING' }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'RESET_ONBOARDING' };

const initialState: UserState = {
  user: null,
  isOnboarded: false,
  currentStep: 0
};

const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'SET_NAME':
      return {
        ...state,
        user: {
          ...state.user,
          name: action.payload,
          preferredStockTypes: state.user?.preferredStockTypes || [],
          birthYear: state.user?.birthYear || 0,
          birthMonth: state.user?.birthMonth || 0
        } as User
      };

    case 'SET_STOCK_TYPES':
      return {
        ...state,
        user: {
          ...state.user,
          preferredStockTypes: action.payload,
          name: state.user?.name || '',
          birthYear: state.user?.birthYear || 0,
          birthMonth: state.user?.birthMonth || 0
        } as User
      };

    case 'SET_BIRTH_DATE':
      return {
        ...state,
        user: {
          ...state.user,
          birthYear: action.payload.year,
          birthMonth: action.payload.month,
          name: state.user?.name || '',
          preferredStockTypes: state.user?.preferredStockTypes || []
        } as User
      };

    case 'COMPLETE_ONBOARDING':
      return {
        ...state,
        isOnboarded: true
      };

    case 'NEXT_STEP':
      return {
        ...state,
        currentStep: Math.min(state.currentStep + 1, 2)
      };

    case 'PREV_STEP':
      return {
        ...state,
        currentStep: Math.max(state.currentStep - 1, 0)
      };

    case 'RESET_ONBOARDING':
      return {
        user: null,
        isOnboarded: false,
        currentStep: 0
      };

    default:
      return state;
  }
};

const UserContext = createContext<{
  state: UserState;
  dispatch: React.Dispatch<UserAction>;
} | null>(null);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  return (
    <UserContext.Provider value={{ state, dispatch }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
